import numeral from 'numeral'
import { FileWithPath } from 'react-dropzone'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CloseIcon from '@mui/icons-material/Close'

type Props = {
  files: FileWithPath[]
  folders: string[]
  acceptedFiles: File[]
  setFiles: (files: FileWithPath[]) => void
  setFolderSet: (folders: string[]) => void
}

const pdfLessThan15MB = (size: number) => size < numeral('15MB').value()!

const UploadFilesSection = ({
  files,
  folders,
  acceptedFiles,
  setFiles,
  setFolderSet,
}: Props) => {
  const removeFile = (file: FileWithPath) => () => {
    const filteredFiles = files.filter(
      (f) => f.name !== file.name || f.path !== file.path
    )
    const idx = acceptedFiles.findIndex(
      (f) => f.name === file.name && (f as FileWithPath).path === file.path
    )

    if (idx >= 0) {
      acceptedFiles.splice(idx, 1)
    }
    setFiles(filteredFiles)
  }

  const folderContainsPdfLessThan15MB = (path: string) => {
    const filteredFiles = files.filter((file) =>
      file.path?.startsWith(`/${path}`)
    )
    return filteredFiles.every((file) => pdfLessThan15MB(file.size))
  }

  const removeFolder = (startPath: string) => () => {
    const filteredFolders = folders.filter((folder) => folder !== startPath)
    const remainingFiles = files.filter(
      (file) => !file.path?.startsWith(`/${startPath}`)
    )
    acceptedFiles.length = 0
    acceptedFiles.splice(0, acceptedFiles.length)
    remainingFiles.forEach((file) => acceptedFiles.push(file))
    setFolderSet(filteredFolders)
    setFiles(remainingFiles)
  }

  if (!files.length) return null

  return (
    <div className="flex flex-col bg-white rounded-lg p-4 max-h-[calc(100%_-_220px)] overflow-y-auto">
      <ul>
        {folders.length > 0 &&
          folders.map((folder, i) => (
            <li key={i}>
              <div
                className={`flex border-2 ${
                  folderContainsPdfLessThan15MB(folder)
                    ? 'border-[#089797]'
                    : 'border-[#EF4444]'
                }  mb-1 rounded-md justify-between p-2`}
              >
                <div className="flex">
                  <div className="flex text-black font-semibold mr-2 items-center">
                    <FolderOutlinedIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div className="flex text-black items-center text-sm font-semibold">
                    {folder}
                  </div>
                </div>
                <div>
                  <button
                    className="transition ease-in-out delay-100 hover:scale-125"
                    onClick={removeFolder(folder)}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
              {!folderContainsPdfLessThan15MB(folder) && (
                <div className="text-sm text-[#EF4444]">
                  This folder contains file more than 15MB, please delete and
                  upload the folder again.
                </div>
              )}
            </li>
          ))}
        {files
          .filter(
            (file) =>
              !folders.some(
                (path) => file.path?.startsWith(`/${path}`) ?? false
              )
          )
          .map((file) => (
            <li className="mb-1" key={`${file.path}-${file.name}`}>
              <div
                className={`flex border-2 ${
                  pdfLessThan15MB(file.size)
                    ? 'border-[#089797]'
                    : 'border-[#EF4444]'
                } rounded-md justify-between p-2 overflow-hidden`}
              >
                <div className="flex flex-shrink max-w-[90%]">
                  <div className="flex  text-black font-semibold mr-2 text-ellipsis max-w-full white-space-nowrap overflow-hidden items-center">
                    {file.name}
                  </div>
                  <div className="flex text-black items-center text-sm font-light">
                    {numeral(file.size).format('0b')}
                  </div>
                </div>

                <div className="flex">
                  <button
                    className="transition ease-in-out delay-100 hover:scale-125"
                    onClick={removeFile(file)}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
              {!pdfLessThan15MB(file.size) && (
                <div className="text-sm text-[#EF4444]">
                  This file is over 15MB, please delete and upload another file.
                </div>
              )}
            </li>
          ))}
      </ul>
    </div>
  )
}

export default UploadFilesSection
